import {
  concatTransformationMatrix,
  decodePDFRawStream,
  degrees,
  drawObject,
  EncryptedPDFError,
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFHexString,
  PDFName,
  PDFNumber,
  PDFPage,
  PDFRawStream,
  PDFStream,
  PDFString,
  popGraphicsState,
  pushGraphicsState,
} from 'pdf-lib'

import {
  AddendumAttachmentAnchor,
  addendumAttachmentAnchorUri,
  parseAddendumAttachmentAnchorUri,
} from './attachmentAnchors'

type Rectangle = { x: number; y: number; width: number; height: number }
type AttachmentMarker = AddendumAttachmentAnchor & {
  page: PDFPage
  annotationIndex: number
  rectangle: Rectangle
}

function readNumbers(array: PDFArray, length: number): number[] {
  if (array.size() !== length) throw new Error('Invalid PDF geometry')
  const values = Array.from({ length }, (_, index) =>
    array.lookup(index, PDFNumber).asNumber()
  )
  if (values.some((value) => !Number.isFinite(value)))
    throw new Error('Invalid PDF geometry')
  return values
}

function readRectangle(array: PDFArray): Rectangle {
  const [x1, y1, x2, y2] = readNumbers(array, 4)
  const rectangle = {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  }
  if (rectangle.width <= 0 || rectangle.height <= 0)
    throw new Error('Invalid PDF rectangle')
  return rectangle
}

function getPageGeometry(page: PDFPage) {
  const crop = page.getCropBox()
  if (
    ![crop.x, crop.y, crop.width, crop.height].every(Number.isFinite) ||
    crop.width <= 0 ||
    crop.height <= 0
  ) {
    throw new Error('Invalid PDF CropBox')
  }
  const rotation = ((page.getRotation().angle % 360) + 360) % 360
  if (![0, 90, 180, 270].includes(rotation))
    throw new Error('Unsupported PDF page rotation')
  return { crop, rotation }
}

function annotationAppearance(annotation: PDFDict): PDFStream {
  const appearances = annotation.lookup(PDFName.of('AP'), PDFDict)
  let normal = appearances.lookup(PDFName.of('N'))
  if (normal instanceof PDFDict) {
    const state =
      annotation.lookupMaybe(PDFName.of('AS'), PDFName) ?? PDFName.of('Off')
    normal = normal.lookup(state)
  }
  if (!(normal instanceof PDFStream))
    throw new Error('PDF annotation has no usable appearance')
  if (normal instanceof PDFRawStream) decodePDFRawStream(normal).decode()
  return normal
}

function appearanceBounds(appearance: PDFStream) {
  const [x1, y1, x2, y2] = readNumbers(
    appearance.dict.lookup(PDFName.of('BBox'), PDFArray),
    4
  )
  const matrix = appearance.dict.lookupMaybe(PDFName.of('Matrix'), PDFArray)
  const [a, b, c, d, e, f] = matrix
    ? readNumbers(matrix, 6)
    : [1, 0, 0, 1, 0, 0]
  const points = [
    [x1, y1],
    [x1, y2],
    [x2, y1],
    [x2, y2],
  ]
  const xs = points.map(([x, y]) => a * x + c * y + e)
  const ys = points.map(([x, y]) => b * x + d * y + f)
  const left = Math.min(...xs)
  const bottom = Math.min(...ys)
  const width = Math.max(...xs) - left
  const height = Math.max(...ys) - bottom
  if (width <= 0 || height <= 0)
    throw new Error('Invalid PDF annotation appearance geometry')
  return { left, bottom, width, height }
}

function flattenAnnotation(page: PDFPage, annotation: PDFDict): void {
  const rectangle = readRectangle(
    annotation.lookup(PDFName.of('Rect'), PDFArray)
  )
  const appearance = annotationAppearance(annotation)
  const { left, bottom, width, height } = appearanceBounds(appearance)
  const scaleX = rectangle.width / width
  const scaleY = rectangle.height / height
  const context = page.doc.context
  const reference =
    context.getObjectRef(appearance) ?? context.register(appearance)
  const name = page.node.newXObject('AddendumAnnotation', reference)
  page.pushOperators(
    pushGraphicsState(),
    concatTransformationMatrix(
      scaleX,
      0,
      0,
      scaleY,
      rectangle.x - left * scaleX,
      rectangle.y - bottom * scaleY
    ),
    drawObject(name),
    popGraphicsState()
  )
}

function hasVisibleLinkBorder(annotation: PDFDict): boolean {
  const color = annotation.lookupMaybe(PDFName.of('C'), PDFArray)
  if (color?.size() === 0) return false
  const borderStyle = annotation.lookupMaybe(PDFName.of('BS'), PDFDict)
  if (borderStyle) {
    return (
      (borderStyle.lookupMaybe(PDFName.of('W'), PDFNumber)?.asNumber() ?? 1) > 0
    )
  }
  const border = annotation.lookupMaybe(PDFName.of('Border'), PDFArray)
  return border && border.size() > 2
    ? border.lookup(2, PDFNumber).asNumber() > 0
    : false
}

function isVisibleAnnotation(annotation: PDFDict): boolean {
  const subtype = annotation.lookupMaybe(PDFName.of('Subtype'), PDFName)
  if (subtype === PDFName.of('Popup')) return false
  const flags =
    annotation.lookupMaybe(PDFName.of('F'), PDFNumber)?.asNumber() ?? 0
  const invisibleHiddenOrNoView = 1 | 2 | 32
  if ((flags & invisibleHiddenOrNoView) !== 0) return false
  return (
    subtype !== PDFName.of('Link') ||
    annotation.has(PDFName.of('AP')) ||
    hasVisibleLinkBorder(annotation)
  )
}

function flattenSourceAnnotations(document: PDFDocument): void {
  const pageAnnotations = document.getPages().map((page) => {
    const sourceAnnotations = page.node.Annots()
    const annotations: PDFDict[] = []
    for (
      let index = 0;
      sourceAnnotations && index < sourceAnnotations.size();
      index += 1
    ) {
      const annotation = sourceAnnotations.lookup(index, PDFDict)
      if (isVisibleAnnotation(annotation)) annotations.push(annotation)
    }
    return { page, annotations }
  })
  const missingWidgetAppearance = pageAnnotations.some(({ annotations }) =>
    annotations.some(
      (annotation) =>
        annotation.lookupMaybe(PDFName.of('Subtype'), PDFName) ===
          PDFName.of('Widget') && !annotation.has(PDFName.of('AP'))
    )
  )
  try {
    if (missingWidgetAppearance) document.getForm().updateFieldAppearances()
    for (const { page, annotations } of pageAnnotations) {
      for (const annotation of annotations) flattenAnnotation(page, annotation)
    }
  } catch {
    throw new Error(
      'This PDF has unsupported annotation appearances. Export/flatten annotations before uploading'
    )
  }
  for (const { page } of pageAnnotations) {
    page.node.delete(PDFName.of('Annots'))
    // pdf-lib cannot embed a blank page unless it has a content stream.
    if (!page.node.Contents())
      page.pushOperators(pushGraphicsState(), popGraphicsState())
  }
}

async function loadSourcePdf(
  bytes: Uint8Array | ArrayBuffer
): Promise<PDFDocument> {
  const sourceBytes = new Uint8Array(bytes).slice()
  let footerEnd = sourceBytes.length
  while (
    footerEnd > 0 &&
    [0, 9, 10, 12, 13, 32].includes(sourceBytes[footerEnd - 1])
  )
    footerEnd -= 1
  const footer = new TextDecoder().decode(
    sourceBytes.subarray(Math.max(0, footerEnd - 5), footerEnd)
  )
  if (footer !== '%%EOF') throw new Error('Incomplete or corrupt addendum PDF')
  let document: PDFDocument
  try {
    document = await PDFDocument.load(sourceBytes, {
      throwOnInvalidObject: true,
    })
  } catch (error) {
    // pdf-lib's ES5 error subclasses do not retain their constructor prototype.
    if (
      error instanceof EncryptedPDFError ||
      (error instanceof Error &&
        error.message === new EncryptedPDFError().message)
    ) {
      throw new Error(
        'This PDF is password protected. Export an unprotected PDF before uploading.'
      )
    }
    throw error
  }
  const pageCount = document.getPageCount()
  if (pageCount < 1 || pageCount > 100)
    throw new Error('Addendum PDFs must contain between 1 and 100 pages')
  for (const page of document.getPages()) getPageGeometry(page)
  flattenSourceAnnotations(document)
  return document
}

function pageBoundingBox(page: PDFPage) {
  const { crop } = getPageGeometry(page)
  return {
    left: crop.x,
    bottom: crop.y,
    right: crop.x + crop.width,
    top: crop.y + crop.height,
  }
}

export async function inspectAttachmentPdf(
  bytes: Uint8Array | ArrayBuffer
): Promise<number> {
  const document = await loadSourcePdf(bytes)
  const validationDocument = await PDFDocument.create()
  const pages = document.getPages()
  await validationDocument.embedPages(pages, pages.map(pageBoundingBox))
  await validationDocument.flush()
  return document.getPageCount()
}

function findAttachmentMarkers(document: PDFDocument): AttachmentMarker[] {
  const markers: AttachmentMarker[] = []
  const seen = new Set<string>()
  for (const page of document.getPages()) {
    const annotations = page.node.Annots()
    for (let index = 0; annotations && index < annotations.size(); index += 1) {
      const annotation = annotations.lookup(index, PDFDict)
      if (
        annotation.lookupMaybe(PDFName.of('Subtype'), PDFName) !==
        PDFName.of('Link')
      )
        continue
      const action = annotation.lookupMaybe(PDFName.of('A'), PDFDict)
      if (action?.lookupMaybe(PDFName.of('S'), PDFName) !== PDFName.of('URI'))
        continue
      const uri = action.lookup(PDFName.of('URI'))
      if (!(uri instanceof PDFString || uri instanceof PDFHexString)) continue
      const anchor = parseAddendumAttachmentAnchorUri(uri.decodeText())
      if (!anchor) continue
      const key = addendumAttachmentAnchorUri(
        anchor.attachmentId,
        anchor.sourcePageIndex
      )
      if (seen.has(key)) throw new Error('Duplicate addendum attachment marker')
      seen.add(key)
      markers.push({
        ...anchor,
        page,
        annotationIndex: index,
        rectangle: readRectangle(
          annotation.lookup(PDFName.of('Rect'), PDFArray)
        ),
      })
    }
  }
  return markers
}

async function drawAttachmentPage(
  document: PDFDocument,
  marker: AttachmentMarker,
  sourcePage: PDFPage
): Promise<void> {
  const { crop, rotation } = getPageGeometry(sourcePage)
  const embedded = await document.embedPage(
    sourcePage,
    pageBoundingBox(sourcePage)
  )
  const sideways = rotation === 90 || rotation === 270
  const width = sideways ? crop.height : crop.width
  const height = sideways ? crop.width : crop.height
  const scale = Math.min(
    marker.rectangle.width / width,
    marker.rectangle.height / height
  )
  const left = marker.rectangle.x + (marker.rectangle.width - width * scale) / 2
  const bottom =
    marker.rectangle.y + (marker.rectangle.height - height * scale) / 2
  const xOffset =
    rotation === 180 ? crop.width : rotation === 270 ? crop.height : 0
  const yOffset =
    rotation === 90 ? crop.width : rotation === 180 ? crop.height : 0
  marker.page.drawPage(embedded, {
    x: left + xOffset * scale,
    y: bottom + yOffset * scale,
    xScale: scale,
    yScale: scale,
    rotate: degrees(-rotation),
  })
}

export async function composeAddendumPdfs(
  blob: Blob,
  pdfSources: ReadonlyMap<string, Uint8Array>
): Promise<Blob> {
  const document = await PDFDocument.load(await blob.arrayBuffer(), {
    throwOnInvalidObject: true,
  })
  const markers = findAttachmentMarkers(document)
  if (markers.length === 0 && pdfSources.size === 0) return blob
  const sources = new Map<string, PDFDocument>()
  for (const [attachmentId, bytes] of pdfSources)
    sources.set(attachmentId, await loadSourcePdf(bytes))
  for (const marker of markers) {
    const source = sources.get(marker.attachmentId)
    if (!source) throw new Error('Missing addendum PDF source')
    const sourcePage = source.getPages()[marker.sourcePageIndex]
    if (!sourcePage) throw new Error('Invalid addendum PDF source page')
    await drawAttachmentPage(document, marker, sourcePage)
  }
  for (const [attachmentId, source] of sources) {
    if (
      markers.filter((marker) => marker.attachmentId === attachmentId)
        .length !== source.getPageCount()
    ) {
      throw new Error('Addendum PDF pages are missing from the report')
    }
  }
  for (const marker of [...markers].reverse())
    marker.page.node.Annots()?.remove(marker.annotationIndex)
  const bytes = await document.save({ updateFieldAppearances: false })
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
}
